/**
 * Langues de l'assistant IA (ChatKit), et repli unique.
 *
 * Une seule liste pour tous les clients TypeScript : l'extension la lit pour la langue
 * de son interface, le front la lit pour valider ce que l'extension lui envoie ET pour
 * poser l'en-tête `Accept-Language` de ses requêtes vers le service ChatKit.
 *
 * ⚠️ Le service Python en tient forcément une copie, `SUPPORTED_LOCALES` dans
 * `chatkit/src/translations.py` (dérivée de ses catalogues de traduction). Les deux
 * doivent rester identiques : c'est ce fichier-ci qui décide de ce qui part sur le
 * réseau, et le serveur qui décide de ce qu'il sait rendre.
 *
 * ⚠️ À ne pas confondre avec `UserLocale` (`User.locale` en base), qui vaut pour le
 * compte et ne connaît pas `nl` : la langue de l'assistant vient de l'interface du
 * client, pas de la fiche utilisateur.
 */
export const ASSISTANT_LOCALES = ['de', 'en', 'es', 'fr', 'it', 'nl', 'pt'] as const;

export type AssistantLocale = (typeof ASSISTANT_LOCALES)[number];

/**
 * Repli unique, partagé avec `DEFAULT_LOCALE` côté serveur. Avant, chaque maillon avait
 * le sien (`en` dans l'extension et `app.py`, `fr` dans les flows) : un même utilisateur
 * sans langue détectable obtenait l'anglais ou le français selon le chemin de code.
 */
export const DEFAULT_ASSISTANT_LOCALE: AssistantLocale = 'en';

export function isAssistantLocale(value: string | undefined): value is AssistantLocale {
  return value !== undefined && (ASSISTANT_LOCALES as readonly string[]).includes(value);
}

/**
 * Normalise ce qu'annonce un client (`de`, `de-DE`, `nl-BE`, `fr_FR`…) vers une langue
 * que l'assistant sait parler, ou le repli.
 */
export function resolveAssistantLocale(raw: string | undefined): AssistantLocale {
  if (raw === undefined || raw === '') {
    return DEFAULT_ASSISTANT_LOCALE;
  }
  // `de-DE`, `de_DE` et `de` désignent la même langue d'assistant : seule la sous-balise
  // primaire compte, comme côté serveur (`normalize_accept_language`).
  const primary = raw.split(/[-_]/)[0].toLowerCase();
  return isAssistantLocale(primary) ? primary : DEFAULT_ASSISTANT_LOCALE;
}
